import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'

import { Box, FormHelperText, FormLabel, Grid, Typography } from '@material-ui/core'

import { IOSSwitch } from '../IOSSwitch'
import { DefaultSelect } from '../DefaultSelect'
import { MoySkladDataLoadingComponent } from '../MoySkladDataLoading'

import { useLogic } from '../../hooks/storeHook'
import { useMessages } from '../../hooks/snackbarHooks'
import { useBoolState, useLazyEffect } from '../../hooks/commonHooks'
import { useSettingsPageStyles } from '../../hooks/settingsPageStylesHook'

import { StoreAccountModel } from '../../types/accountProps'
import { ReturnsSettings } from '../../server/mpsklad_core/Models/ReturnsSettings'
import { MoySkladApiEntityOption } from '../../server/mpsklad_core/Models/MoySkladApiEntityOption'

export type MsStoresLoadingProps = {
  msStores: MoySkladApiEntityOption[] | undefined

  loadMsStores: () => Promise<void>
}

export type StoreReturnsSettingsProps =
  MsStoresLoadingProps
  & {
  title: string
  account: StoreAccountModel
  saveSettings: (settings: ReturnsSettings) => Promise<void>
}

export const StoreReturnsSettings =
  observer(({
              title,
              msStores,
              loadMsStores,
              account,
              saveSettings
            }: StoreReturnsSettingsProps) => {
    const {showSuccess, showError} = useMessages()
    const logic = useLogic()
    const [isLoading, setIsLoading, setNotLoading] = useBoolState()
    const [isMsStoresLoading, setMsStoresLoading, setMsStoresNotLoading] = useBoolState()
    const [msSalesReturnStoreId, setMsSalesReturn] = useState(account.msSalesReturnStoreId)
    const [autoReturnsEnabled, setAutoReturnsEnabled] = useState(account.isAutoReturnEnabled)
    const [autoRemoveCancelledMsOrderDocs, setAutoRemoveCancelledMsOrderDocs] =
      useState(account.autoRemoveCancelledMsOrderDocs)

    const classes = useSettingsPageStyles()

    useLazyEffect(() => {
      setMsSalesReturn(account.msSalesReturnStoreId)
      setAutoReturnsEnabled(account.isAutoReturnEnabled)
      setAutoRemoveCancelledMsOrderDocs(account.autoRemoveCancelledMsOrderDocs)
    }, [account.id])

    useEffect(() => {
      if (!msStores) {
        setMsStoresLoading()
        loadMsStores().finally(setMsStoresNotLoading)
      }
    }, [msStores, loadMsStores, setMsStoresLoading, setMsStoresNotLoading])

    const onSubmit = async () => {
      try {
        setIsLoading()

        await saveSettings({
          accountId: account.id,
          isAutoReturnEnabled: autoReturnsEnabled,
          msSalesReturnStoreId,
          autoRemoveCancelledMsOrderDocs
        })

        setTimeout(logic.loadUserAccounts)
        showSuccess('Настройки возвратов обновлены!')
      } catch {
        showError('Произошла ошибка при настройке возвратов!')
      } finally {
        setNotLoading()
      }
    }

    const msStoreOptions = msStores?.map(store => ({
      value: store.id,
      label: store.name
    })) ?? []

    if (isMsStoresLoading) {
      return <MoySkladDataLoadingComponent/>
    }

    return (
      <Box className={classes.pageContainer}>
        <Typography className={classes.header}>{title}</Typography>

        <Grid container spacing={3}>
          {
            account.isDeliveryFromSellerWarehouse &&
            <Grid item xs={7}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box>
                    <FormLabel component="legend" className={classes.label}>
                      <span className={classes.dash}>—</span>
                      Склад возвратов МоегоСклада
                    </FormLabel>
                  </Box>

                  <DefaultSelect
                    fullWidth
                    withUnselectedValue
                    placeholder="Выберите склад"
                    value={msSalesReturnStoreId}
                    options={msStoreOptions}
                    onChange={setMsSalesReturn}
                  />

                  <Box marginTop={2}>
                    <FormHelperText>
                      Укажите отдельный склад для возвратов, отличный от основных настроек складов.
                    </FormHelperText>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          }

          <Grid item xs={7}>
            <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
              <Grid item className={classes.root} xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <FormLabel className={classes.unifiedPricesLabel}>Создание документов о возврате</FormLabel>

                  <IOSSwitch checked={autoReturnsEnabled} onChange={() => setAutoReturnsEnabled(prev => !prev)}/>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={7}>
            <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
              <Grid item className={classes.root} xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <FormLabel className={classes.unifiedPricesLabel}>Удалять документы при статусе Отменён</FormLabel>

                  <IOSSwitch
                    checked={autoRemoveCancelledMsOrderDocs}
                    onChange={() => setAutoRemoveCancelledMsOrderDocs(prev => !prev)}/>
                </Box>

                <FormHelperText className={classes.helperText}>
                  В заказе МоегоСклада будут удалены Отгрузка и Входящий платёж, если они были привязаны.
                </FormHelperText>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box display="flex">
          <button className={clsx(classes.saveButton, 'default-button')} disabled={isLoading} onClick={onSubmit}>
            <p>{isLoading ? 'Сохраняем...' : 'Сохранить'}</p>
          </button>
        </Box>
      </Box>
    )
  })